import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react'
import CONFIG from '../../app-configs'
import { ApiAuthStrategies } from '../shared/ApiAuthStrategies'
import {
  type PortfolioSettlementDto,
  type PortfolioSettlementProjectDto
} from '../../api-schema-types/asset-holdings/AdminPortfolioSettlementDomainDtos'
import { portfolioSettlementsApiMapper } from './portfolioSettlementsApiMapper'

export interface settlementPortfolioProps {
  portfolioSettlementId: number
}
export interface settlementPortfolioProjectProps {
  portfolioSettlementProjectId: number
}
export const portfolioSettlementsApi = createApi({
  reducerPath: 'portfolioSettlementsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${CONFIG.PROXIMA_BACK_END_V2_BASE_API_URL}/asset-holdings/asset-holders/settlements`,
    prepareHeaders: (headers: Headers, { getState }) => {
      const authToken = ApiAuthStrategies.getBearerTokenHeader()
      headers.set('authorization', authToken)
      return headers
    }
  }),

  tagTypes: ['PortfolioSettlements', 'PortfolioSettlement', 'PortfolioSettlementProject', 'PortfolioSettlementProjects'],

  endpoints: (builder) => ({
    // ================================================================================================================
    getPortfolios: builder.query({
      query: () => ({
        url: '/portfolios'
      }),
      providesTags: ['PortfolioSettlements'],
      transformResponse: (response: PortfolioSettlementDto[], meta, arg) => portfolioSettlementsApiMapper().portfoliosMapper(response, meta, arg)
    }),

    getSettlementPortfolio: builder.query({
      query: ({ portfolioSettlementId }: settlementPortfolioProps) => ({
        url: `/portfolios/${portfolioSettlementId}`
      }),
      providesTags: ['PortfolioSettlement', 'PortfolioSettlementProjects'],
      transformResponse: (response: PortfolioSettlementDto, meta, arg) => portfolioSettlementsApiMapper().settlementPortfolioMapper(response, meta, arg)
    }),

    getSettlementPortfolioProject: builder.query({
      query: ({ portfolioSettlementProjectId }: settlementPortfolioProjectProps) => ({
        url: `/portfolios/project/${portfolioSettlementProjectId}`
      }),
      providesTags: ['PortfolioSettlementProject'],
      transformResponse: (response: PortfolioSettlementProjectDto, meta, arg) => portfolioSettlementsApiMapper().settlementPortfolioProjectMapper(response, meta, arg)
    }),

    // ================================================================================================================

    updateSettlementPortfolioProject: builder.mutation<PortfolioSettlementProjectDto, Partial<PortfolioSettlementProjectDto>>({
      query: ({ ...patch }) => ({
        url: `/portfolios/project/${patch.portfolioSettlementProjectId}`,
        method: 'put',
        body: patch
      }),
      invalidatesTags: ['PortfolioSettlement', 'PortfolioSettlementProject', 'PortfolioSettlementProjects']
    }),

    // ================================================================================================================

    createAndProcessSettlementInstructions: builder.mutation({
      query: ({ portfolioSettlementId }) => ({
        url: `/portfolios/${portfolioSettlementId}/trigger-create-and-process`,
        method: 'post'
      }),
      invalidatesTags: ['PortfolioSettlement', 'PortfolioSettlementProjects']
    })

    // ================================================================================================================

  })
})

export const useGetPortfoliosQuery = portfolioSettlementsApi.endpoints.getPortfolios.useQuery
export const useGetSettlementPortfolioQuery = portfolioSettlementsApi.endpoints.getSettlementPortfolio.useQuery
export const useGetSettlementPortfolioProjectQuery = portfolioSettlementsApi.endpoints.getSettlementPortfolioProject.useQuery
export const useUpdateSettlementPortfolioProjectMutation = portfolioSettlementsApi.endpoints.updateSettlementPortfolioProject.useMutation

export const useCreateAndProcessSettlementInstructionsMutation = portfolioSettlementsApi.endpoints.createAndProcessSettlementInstructions.useMutation
